import { Link } from "react-router-dom";
import { Heart, Leaf, Users, ArrowRight } from "lucide-react";
import Newsletter from "@/components/Newsletter";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6 animate-fade-up">
          <span className="font-sans text-sm uppercase tracking-wider text-[#D96B43]">
            Our Story
          </span>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-[#2C3529] mt-4 leading-[1.05]">
            We believe every animal deserves a gentle life
          </h1>
          <p className="font-sans text-lg text-[#576054] mt-6 leading-relaxed">
            PawJournal began with a simple idea: that caring for a pet should
            feel warm, not overwhelming. We're a small collective of vets,
            behaviourists, and lifelong animal lovers who wanted to share
            honest, kind guidance — free of jargon and fear.
          </p>
          <p className="font-sans text-lg text-[#576054] mt-4 leading-relaxed">
            Every article we publish is written with one question in mind: how
            can we help this animal, and the person who loves them, live a
            little more happily together?
          </p>
        </div>
        <div className="md:col-span-6 animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="overflow-hidden rounded-[2.5rem] aspect-[4/5]">
            <img
              src="https://images.unsplash.com/photo-1557495235-340eb888a9fb"
              alt="A woman embracing her dog"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { icon: Heart, title: "Compassion first", text: "We lead with empathy — for animals and the people who care for them." },
          { icon: Leaf, title: "Honest guidance", text: "No scare tactics, no fluff. Just practical, science-informed advice." },
          { icon: Users, title: "A real community", text: "Thousands of pet parents learning, sharing, and growing together." },
        ].map((v, i) => (
          <div
            key={i}
            className="bg-[#FAF9F6] rounded-[2rem] p-8 border border-[#EAE4D9] animate-fade-up"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-[#D96B43]/12 text-[#D96B43] mb-5">
              <v.icon size={24} strokeWidth={1.5} />
            </span>
            <h3 className="font-serif text-2xl text-[#2C3529]">{v.title}</h3>
            <p className="font-sans text-[#576054] mt-2 leading-relaxed">{v.text}</p>
          </div>
        ))}
      </section>

      <section className="bg-[#2C3529] text-[#FAF9F6] rounded-[3rem] mx-4 md:mx-8 my-12 px-8 py-20 text-center">
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight max-w-2xl mx-auto">
          Ready to become a more confident pet parent?
        </h2>
        <p className="font-sans text-white/80 mt-5 max-w-xl mx-auto leading-relaxed">
          Dive into our journal for warm, trustworthy guidance on every kind of
          companion.
        </p>
        <Link
          to="/blog"
          data-testid="about-cta"
          className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#D96B43] text-white px-8 py-3.5 font-sans font-medium hover:-translate-y-1 hover:bg-[#C25D3A] transition-all duration-300"
        >
          Start reading <ArrowRight size={18} />
        </Link>
      </section>

      <Newsletter />
    </div>
  );
}
